import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '../../cn';
import { NOTIFICATION_VARIANTS } from '../../constants';

const VARIANTS = {
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    icon: 'text-blue-400',
    text: 'text-blue-700',
    iconPath: (
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
    )
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-400',
    icon: 'text-green-400',
    text: 'text-green-700',
    iconPath: (
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
    )
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-400',
    icon: 'text-yellow-400',
    text: 'text-yellow-700',
    iconPath: (
      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    )
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-400',
    icon: 'text-red-400',
    text: 'text-red-700',
    iconPath: (
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
    )
  },
  neutral: {
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    icon: 'text-gray-400',
    text: 'text-gray-700',
    iconPath: (
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
    )
  }
};

const NotificationBar = ({
  variant = 'info',
  children,
  title,
  showIcon = true,
  className = '',
  role = 'status'
}) => {
  const config = VARIANTS[variant] || VARIANTS.info;

  return (
    <div
      role={role}
      className={cn(config.bg, 'border-l-4', config.border, 'p-4 rounded-r-lg', className)}
    >
      <div className="flex items-start">
        {showIcon && (
          <div className="flex-shrink-0">
            <svg className={cn('h-5 w-5', config.icon)} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              {config.iconPath}
            </svg>
          </div>
        )}
        <div className={cn(showIcon ? 'ml-3' : '', 'flex-1')}>
          <p className={cn('text-sm', config.text)}>
            {title && <strong>{title} </strong>}
            {children}
          </p>
        </div>
      </div>
    </div>
  );
};

NotificationBar.displayName = 'NotificationBar';

NotificationBar.propTypes = {
  variant: PropTypes.oneOf(NOTIFICATION_VARIANTS),
  children: PropTypes.node,
  title: PropTypes.string,
  showIcon: PropTypes.bool,
  className: PropTypes.string,
  role: PropTypes.string
};

export default NotificationBar;
